/* eslint-disable no-console */
import {
  Box,
  HStack,
  Text,
  VStack,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import { ArtifactEditor } from './ArtifactEditor';

type JSONEditorProps = {
  contents: object;
  nodeId: string;
  schema: object;
  children: React.ReactNode;
  autoSave: boolean;
  onSave: (nodeId: string, newValue: object) => void;
  readOnly: boolean;
};
export const JSONEditor = ({
  contents,
  nodeId,
  schema,
  children,
  autoSave,
  onSave,
  readOnly,
}: JSONEditorProps) => {
  const [stateNodeId, setStateNodeId] = useState<string>(nodeId);
  const [errors, setErrors] = useState<string[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [value, setValue] = useState<string>(JSON.stringify(contents, null, 4));

  // Theme-aware colors for Actions panel
  const actionsBg = useColorModeValue('gray.50', 'gray.700');
  const actionsBorderColor = useColorModeValue('gray.200', 'gray.600');
  const actionsTextColor = useColorModeValue('gray.700', 'gray.200');

  useEffect(() => {
    setStateNodeId(nodeId);
    setErrors([]);
    setDirty(false);
    setValue(JSON.stringify(contents, null, 4));
  }, [contents, nodeId]);

  // save change can be called with updated value (newValue)
  // if not provided, it will use the value from state.
  const saveChanges = (newValue: string) => {
    if (stateNodeId !== nodeId) {
      return;
    }

    if (errors.length !== 0) {
      return;
    }

    try {
      const configuration = JSON.parse(newValue);
      onSave(nodeId, configuration);
      setDirty(false);
    } catch (e) {
      setErrors(errors.concat('failed to parse'));
    }
  };

  const onChange = (newValue: string) => {
    if (stateNodeId !== nodeId) {
      return;
    }

    setDirty(true);
    setValue(newValue);

    if (errors.length !== 0) {
      return;
    }

    if (autoSave) {
      saveChanges(newValue);
    }
  };

  const maxErrorsToShow = 3;
  const topErrors = errors.slice(0, maxErrorsToShow);
  const moreErrors = Math.max(errors.length - maxErrorsToShow, 0);

  return (
    <VStack width="100%" height="100%" spacing={0} alignItems="stretch">
      {/* Top section: Action buttons and status */}
      {!readOnly && (
        <Box
          px="12px"
          py="8px"
          borderBottom="2px solid"
          borderColor="gray.200"
          mb="10px"
        >
          <VStack spacing={3} alignItems="stretch">
            {/* Action buttons section - distinct from status */}
            {children && (
              <Box
                p={3}
                bg={actionsBg}
                borderRadius="md"
                border="1px solid"
                borderColor={actionsBorderColor}
              >
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={actionsTextColor}
                  mb={2}
                >
                  Actions
                </Text>
                <HStack spacing={2} flexWrap="wrap">
                  {children}
                </HStack>
              </Box>
            )}
            {/* Status indicators row */}
            <HStack spacing={2} flexWrap="wrap">
              {errors.length === 0 && (
                <Badge
                  colorScheme="green"
                  variant="solid"
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  ✓ Valid
                </Badge>
              )}
              {!dirty && errors.length === 0 && (
                <Badge
                  colorScheme="blue"
                  variant="solid"
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  ✓ Saved
                </Badge>
              )}
              {dirty && (
                <Badge
                  colorScheme="orange"
                  variant="solid"
                  fontSize="sm"
                  px={3}
                  py={1}
                  cursor="pointer"
                  onClick={() => saveChanges(value)}
                  _hover={{ opacity: 0.8 }}
                >
                  ⚠ Unsaved - Click to save
                </Badge>
              )}
            </HStack>
          </VStack>
        </Box>
      )}
      {/* Errors section (only show when there are errors) */}
      {!readOnly && errors.length > 0 && (
        <Box
          px="12px"
          py="8px"
          bg="red.50"
          borderBottom="1px solid"
          borderColor="red.200"
          mb="10px"
        >
          <VStack
            align="stretch"
            spacing={1}
            maxHeight="150px"
            overflowY="auto"
          >
            <Text fontSize="xs" fontWeight="bold" color="red.700" mb={1}>
              Validation Errors:
            </Text>
            {topErrors.map((error) => (
              <Box
                key={error}
                px={2}
                py={1}
                bg="white"
                borderLeft="3px solid"
                borderColor="red.500"
                borderRadius="sm"
              >
                <Text fontSize="xs" color="red.800">
                  {error}
                </Text>
              </Box>
            ))}
            {moreErrors > 0 && (
              <Text fontSize="xs" color="red.600" fontStyle="italic" mt={1}>
                + {moreErrors} more error{moreErrors > 1 ? 's' : ''}
              </Text>
            )}
          </VStack>
        </Box>
      )}
      {/* Read-only mode: show children at top with distinct styling */}
      {readOnly && children && (
        <Box px="12px" py="8px" borderBottom="1px solid" borderColor="gray.200">
          <Box
            p={3}
            bg={actionsBg}
            borderRadius="md"
            border="1px solid"
            borderColor={actionsBorderColor}
          >
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color={actionsTextColor}
              mb={2}
            >
              Actions
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {children}
            </HStack>
          </Box>
        </Box>
      )}
      {/* Main editor - fills remaining space */}
      <Box
        id={nodeId}
        flex={1}
        minHeight="400px"
        overflow="hidden"
        px="12px"
        pb="12px"
      >
        <Box
          width="100%"
          height="100%"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          overflow="hidden"
        >
          <ArtifactEditor
            id={nodeId}
            value={value}
            onChange={(output) => {
              if (typeof output === 'string') {
                onChange(output);
              }
            }}
            onValidate={(newMarkers) => {
              setErrors(newMarkers.map((marker) => marker.message));
            }}
            schema={schema}
            readOnly={readOnly}
          />
        </Box>
      </Box>
    </VStack>
  );
};
